import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';

const registerSchema = z.object({
  firstName: z.string().min(1, 'El nombre es obligatorio'),
  lastName: z.string().min(1, 'Los apellidos son obligatorios'),
  email: z.string().email('Email no válido'),
  phone: z.string().optional(),
  password: z
    .string()
    .min(8, 'Mínimo 8 caracteres')
    .regex(/[a-zA-Z]/, 'Debe contener al menos una letra')
    .regex(/[0-9]/, 'Debe contener al menos un número'),
  confirmPassword: z.string(),
}).refine((data) => data.password === data.confirmPassword, {
  message: 'Las contraseñas no coinciden',
  path: ['confirmPassword'],
});

type RegisterForm = z.infer<typeof registerSchema>;

export function Register() {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const { register: registerUser } = useAuth();
  const { register, handleSubmit, watch, formState: { errors, isSubmitting } } = useForm<RegisterForm>({
    resolver: zodResolver(registerSchema),
  });

  const password = watch('password', '');
  const passwordStrength = () => {
    if (!password) return 0;
    let score = 0;
    if (password.length >= 8) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/[0-9]/.test(password)) score++;
    if (/[^a-zA-Z0-9]/.test(password)) score++;
    return score;
  };
  const strength = passwordStrength();
  const strengthColors = ['#ef4444', '#f97316', '#eab308', '#22c55e'];
  const strengthLabels = ['Muy débil', 'Débil', 'Buena', 'Fuerte'];

  const onSubmit = async (data: RegisterForm) => {
    const { confirmPassword, ...registerData } = data;
    await registerUser(registerData);
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      fontFamily: "'Inter', sans-serif",
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
        * { box-sizing: border-box; }
        .reg-input {
          width: 100%; padding: 12px 16px; border: 1.5px solid #e2e8f0;
          border-radius: 10px; font-size: .95rem; color: #1e293b;
          outline: none; transition: border-color .2s, box-shadow .2s;
          font-family: 'Inter', sans-serif; background: #f8fafc;
        }
        .reg-input:focus { border-color: #3b82f6; box-shadow: 0 0 0 3px rgba(59,130,246,.1); background: white; }
        .reg-input.error { border-color: #ef4444; }
        .reg-label { display: block; font-size: .82rem; font-weight: 600; color: #475569; margin-bottom: 6px; }
        .reg-error { font-size: .78rem; color: #ef4444; margin-top: 4px; }
        .reg-btn {
          width: 100%; padding: 14px; background: #3b82f6; color: white;
          border: none; border-radius: 10px; font-size: 1rem; font-weight: 700;
          cursor: pointer; transition: background .2s, transform .15s;
          font-family: 'Inter', sans-serif;
        }
        .reg-btn:hover:not(:disabled) { background: #2563eb; transform: translateY(-1px); }
        .reg-btn:disabled { background: #93c5fd; cursor: not-allowed; }
        @media (max-width: 768px) {
          .reg-left { display: none !important; }
          .reg-right { grid-column: 1 / -1 !important; }
        }
      `}</style>

      {/* LEFT — Visual */}
      <div className="reg-left" style={{
        background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        padding: '60px 48px', position: 'relative', overflow: 'hidden',
      }}>
        <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse at 30% 20%, rgba(59,130,246,.2) 0%, transparent 60%)' }}></div>
        <div style={{ position: 'relative', zIndex: 1, textAlign: 'center', maxWidth: 400 }}>
          <div style={{ fontSize: '3rem', marginBottom: 24 }}>💪</div>
          <h2 style={{ color: 'white', fontSize: '2rem', fontWeight: 800, marginBottom: 16, lineHeight: 1.2 }}>
            Lleva tu negocio<br/>al siguiente nivel
          </h2>
          <p style={{ color: '#94a3b8', fontSize: '.95rem', lineHeight: 1.7, marginBottom: 40 }}>
            Únete a más de 2.400 negocios fitness y bienestar que ya usan FitPulse para gestionar sus eventos, clientes y crecer.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {[
              { icon: '📅', text: 'Publica eventos y clases ilimitadas' },
              { icon: '👥', text: 'Gestiona tu equipo y horarios' },
              { icon: '📊', text: 'Analytics y métricas en tiempo real' },
              { icon: '🌐', text: 'Página pública profesional gratis' },
            ].map((item, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, background: 'rgba(255,255,255,.06)', borderRadius: 12, padding: '12px 16px', border: '1px solid rgba(255,255,255,.08)' }}>
                <span style={{ fontSize: '1.2rem' }}>{item.icon}</span>
                <span style={{ color: '#e2e8f0', fontSize: '.88rem', fontWeight: 500 }}>{item.text}</span>
              </div>
            ))}
          </div>
          <div style={{ marginTop: 40, display: 'flex', gap: 24, justifyContent: 'center' }}>
            {[['2.400+', 'Negocios'], ['98%', 'Satisfacción'], ['Gratis', 'Para empezar']].map(([num, label], i) => (
              <div key={i} style={{ textAlign: 'center' }}>
                <div style={{ color: '#60a5fa', fontSize: '1.4rem', fontWeight: 800 }}>{num}</div>
                <div style={{ color: '#64748b', fontSize: '.72rem', marginTop: 2 }}>{label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* RIGHT — Form */}
      <div className="reg-right" style={{
        background: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '40px 24px', overflowY: 'auto',
      }}>
        <div style={{ width: '100%', maxWidth: 440 }}>

          {/* Logo */}
          <div style={{ textAlign: 'center', marginBottom: 32 }}>
            <a href="https://fitpulse.es" style={{ textDecoration: 'none' }}>
              <span style={{ fontSize: '1.4rem', fontWeight: 800, color: '#3b82f6' }}>● Fit</span>
              <span style={{ fontSize: '1.4rem', fontWeight: 800, color: '#1e293b' }}>Pulse</span>
            </a>
            <h1 style={{ fontSize: '1.6rem', fontWeight: 800, color: '#1e293b', marginTop: 16, marginBottom: 6 }}>
              Crea tu cuenta gratis
            </h1>
            <p style={{ color: '#64748b', fontSize: '.88rem' }}>
              ¿Ya tienes cuenta?{' '}
              <Link to="/login" style={{ color: '#3b82f6', fontWeight: 600, textDecoration: 'none' }}>Iniciar sesión</Link>
            </p>
          </div>

          {/* Form */}
          <div style={{ background: 'white', borderRadius: 20, padding: '32px', boxShadow: '0 4px 24px rgba(0,0,0,.06)', border: '1px solid #e2e8f0' }}>
            <form onSubmit={handleSubmit(onSubmit)} style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label className="reg-label">Nombre *</label>
                  <input {...register('firstName')} className={`reg-input ${errors.firstName ? 'error' : ''}`} placeholder="Ahmed" />
                  {errors.firstName && <p className="reg-error">{errors.firstName.message}</p>}
                </div>
                <div>
                  <label className="reg-label">Apellidos *</label>
                  <input {...register('lastName')} className={`reg-input ${errors.lastName ? 'error' : ''}`} placeholder="García" />
                  {errors.lastName && <p className="reg-error">{errors.lastName.message}</p>}
                </div>
              </div>

              <div>
                <label className="reg-label">Email *</label>
                <input {...register('email')} type="email" className={`reg-input ${errors.email ? 'error' : ''}`} placeholder="tu@email.com" />
                {errors.email && <p className="reg-error">{errors.email.message}</p>}
              </div>

              <div>
                <label className="reg-label">Teléfono <span style={{ color: '#94a3b8', fontWeight: 400 }}>(opcional)</span></label>
                <input {...register('phone')} type="tel" className="reg-input" placeholder="+34 600 000 000" />
              </div>

              <div>
                <label className="reg-label">Contraseña *</label>
                <div style={{ position: 'relative' }}>
                  <input {...register('password')} type={showPassword ? 'text' : 'password'} className={`reg-input ${errors.password ? 'error' : ''}`} placeholder="Mínimo 8 caracteres" style={{ paddingRight: 44 }} />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: 0 }}>
                    {showPassword ? <EyeSlashIcon style={{ width: 18 }} /> : <EyeIcon style={{ width: 18 }} />}
                  </button>
                </div>
                {password && (
                  <div style={{ marginTop: 8 }}>
                    <div style={{ display: 'flex', gap: 4, marginBottom: 4 }}>
                      {[0,1,2,3].map(i => (
                        <div key={i} style={{ flex: 1, height: 3, borderRadius: 100, background: i < strength ? strengthColors[strength - 1] : '#e2e8f0', transition: 'background .3s' }} />
                      ))}
                    </div>
                    <p style={{ fontSize: '.72rem', color: strengthColors[strength - 1] || '#94a3b8' }}>
                      {strength > 0 ? strengthLabels[strength - 1] : ''}
                    </p>
                  </div>
                )}
                {errors.password && <p className="reg-error">{errors.password.message}</p>}
              </div>

              <div>
                <label className="reg-label">Confirmar contraseña *</label>
                <div style={{ position: 'relative' }}>
                  <input {...register('confirmPassword')} type={showConfirm ? 'text' : 'password'} className={`reg-input ${errors.confirmPassword ? 'error' : ''}`} placeholder="Repite tu contraseña" style={{ paddingRight: 44 }} />
                  <button type="button" onClick={() => setShowConfirm(!showConfirm)} style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: 0 }}>
                    {showConfirm ? <EyeSlashIcon style={{ width: 18 }} /> : <EyeIcon style={{ width: 18 }} />}
                  </button>
                </div>
                {errors.confirmPassword && <p className="reg-error">{errors.confirmPassword.message}</p>}
              </div>

              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                <input type="checkbox" required style={{ marginTop: 2, flexShrink: 0 }} />
                <label style={{ fontSize: '.82rem', color: '#64748b', lineHeight: 1.5 }}>
                  Acepto los{' '}
                  <Link to="/terms" style={{ color: '#3b82f6', textDecoration: 'none', fontWeight: 600 }}>Términos de servicio</Link>
                  {' '}y la{' '}
                  <Link to="/privacy" style={{ color: '#3b82f6', textDecoration: 'none', fontWeight: 600 }}>Política de privacidad</Link>
                </label>
              </div>

              <button type="submit" disabled={isSubmitting} className="reg-btn">
                {isSubmitting ? '⏳ Creando cuenta...' : '🚀 Crear cuenta gratis'}
              </button>

            </form>
          </div>

          <p style={{ textAlign: 'center', marginTop: 20, fontSize: '.78rem', color: '#94a3b8' }}>
            🔒 Tus datos están seguros y protegidos
          </p>
        </div>
      </div>
    </div>
  );
}
